import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type { XRayAnchor, XRayDestination, XRayEdge, XRayOpenState } from './xrayTypes'
import { calculateEdgeCoordinates, queryTargetCenter } from './xrayGeometry'
import { navigateAndPulse } from './navigateAndPulse'
import { Pressable } from '../Pressable'
import './xrayStyles.css'

interface XRayOverlayProps {
  state: XRayOpenState
  onClose: () => void
}

interface ResolvedEdge {
  edge: XRayEdge
  x1: number
  y1: number
  x2: number
  y2: number
  targetId: string
}

const computeTargetPositions = (anchor: XRayAnchor): Map<string, { x: number; y: number }> => {
  if (typeof document === 'undefined') return new Map<string, { x: number; y: number }>()
  const posMap = new Map<string, { x: number; y: number }>()

  if (anchor.id && anchor.id !== 'canvas') {
    const anchorPos = anchor.screenPosition ?? queryTargetCenter(anchor.id)
    if (anchorPos) posMap.set(anchor.id, anchorPos)
  }

  for (const edge of anchor.edges) {
    if (!posMap.has(edge.fromId)) {
      const p = queryTargetCenter(edge.fromId)
      if (p) posMap.set(edge.fromId, p)
    }
    if (!posMap.has(edge.toId)) {
      const p = queryTargetCenter(edge.toId)
      if (p) posMap.set(edge.toId, p)
    }
  }
  return posMap
}

export const XRayOverlay: React.FC<XRayOverlayProps> = ({ state, onClose }) => {
  const { anchor } = state
  const [targetPositions, setTargetPositions] = useState<Map<string, { x: number; y: number }>>(
    () => computeTargetPositions(anchor),
  )
  const [prevAnchor, setPrevAnchor] = useState(anchor)
  if (prevAnchor !== anchor) {
    setPrevAnchor(anchor)
    setTargetPositions(computeTargetPositions(anchor))
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const resolvedEdges = useMemo<ResolvedEdge[]>(() => {
    const list: ResolvedEdge[] = []
    for (const edge of anchor.edges) {
      let fromPt = targetPositions.get(edge.fromId)
      let toPt = targetPositions.get(edge.toId)
      if (!fromPt && edge.fromId === anchor.id && anchor.screenPosition) fromPt = anchor.screenPosition
      if (!toPt && edge.toId === anchor.id && anchor.screenPosition) toPt = anchor.screenPosition

      if (fromPt && toPt) {
        const coords = calculateEdgeCoordinates(fromPt, toPt)
        const targetId = edge.toId === anchor.id ? edge.fromId : edge.toId
        list.push({ edge, ...coords, targetId })
      }
    }
    return list
  }, [anchor, targetPositions])

  const handleEdgeClick = (targetId: string) => {
    const selector = `[data-checklist-id="${targetId}"], [data-node-id="${targetId}"], [data-shape-id="${targetId}"], [data-goal-id="${targetId}"], [data-task-id="${targetId}"], [data-marker-id="${targetId}"], [id="${targetId}"]`
    navigateAndPulse('', selector)
    onClose()
  }

  const handleDestinationClick = (dest: XRayDestination) => {
    const selector = `[data-checklist-id="${dest.id}"], [data-node-id="${dest.id}"], [data-shape-id="${dest.id}"], [data-field-id="${dest.id}"], [id="${dest.id}"]`
    navigateAndPulse(dest.targetRoute || '', selector)
    onClose()
  }

  if (typeof document === 'undefined') return null

  const hasEdges = anchor.edges.length > 0
  const hasDestinations = anchor.destinations.length > 0
  const isEmpty = !hasEdges && !hasDestinations

  const destPanelStyle: React.CSSProperties = {
    top: anchor.screenPosition ? Math.max(16, anchor.screenPosition.y - 40) : 80,
    left: anchor.screenPosition ? Math.min(window.innerWidth - 300, anchor.screenPosition.x + 60) : window.innerWidth - 300,
  }

  return createPortal(
    <>
      <div className="xray-overlay-backdrop" data-testid="xray-backdrop" onPointerDown={onClose} />
      <svg className="xray-overlay-svg" data-testid="xray-svg">
        {resolvedEdges.map(({ edge, x1, y1, x2, y2, targetId }, index) => {
          const midX = (x1 + x2) / 2
          const midY = (y1 + y2) / 2
          return (
            <g key={`${edge.fromId}-${edge.toId}-${index}`} className="xray-edge-group" data-testid={`xray-edge-${index}`}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} className="xray-edge-hitbox" onClick={(e) => { e.stopPropagation(); handleEdgeClick(targetId) }} />
              <line x1={x1} y1={y1} x2={x2} y2={y2} className="xray-edge-line" />
              {edge.label ? <text x={midX} y={midY - 6} className="xray-edge-label" textAnchor="middle">{edge.label}</text> : null}
            </g>
          )
        })}
      </svg>
      {hasDestinations ? (
        <div className="xray-destinations-panel" style={destPanelStyle} data-testid="xray-destinations" onClick={(e) => e.stopPropagation()}>
          <div className="xray-destinations-header">Materializations</div>
          <ul className="xray-destinations-list">
            {anchor.destinations.map((dest) => (
              <li key={dest.id}>
                <Pressable className="xray-destination-item" data-testid={`xray-dest-${dest.id}`} onClick={() => handleDestinationClick(dest)}>
                  <span>{dest.label}</span>
                  <span className="xray-destination-arrow">→</span>
                </Pressable>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {isEmpty ? (
        <div className="xray-empty-pill" data-testid="xray-empty-state">
          <span>No connections found.</span>
          <Pressable onClick={onClose}>Close</Pressable>
        </div>
      ) : null}
    </>,
    document.body,
  )
}
